import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Link as LinkIcon } from 'lucide-react';
import EligibilityCard from '../components/EligibilityCard';
import EligibilityModal from '../components/EligibilityModal';
import ConfirmDialog from '../components/ConfirmDialog';

const sanitizeAndFormatHtml = (content) => {
    if (!content || typeof content !== 'string') return '';
    let html = content;
    html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
        const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
        if (!attrMatch) return tag;
        const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
        const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
        const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
        return `<a href="${rawUrl}"${targetAttr}>`;
    });
    if (!/<(?:br|p|div)\b/i.test(html)) {
        html = html.replace(/\r?\n/g, '<br />');
    }
    return html;
};

const DEFAULT_CALCULATOR_NOTICE_TEXT = 'Not sure how much home loan you may qualify for? Use our <a href="https://ahamhfc.com/customer-360-eligibility-calculator">home loan eligibility calculator</a> to get an initial estimate before you apply.';

const HomePurchaseLoanEligibilityPage = () => {
    const [criteriaList, setCriteriaList] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Home Purchase Loan Eligibility',
        section_subtitle: 'Eligibility for an Aham home purchase loan is simple, and it is always subject to assessment. Here is what we look at.',
        calculator_notice_text: DEFAULT_CALCULATOR_NOTICE_TEXT
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCriteria, setEditingCriteria] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, criteriaId: null });

    // Link inserter state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const handleInsertLink = () => {
        if (!linkUrl.trim()) {
            toast.error('Please enter a valid URL (e.g. https://ahamhfc.com/customer-360-eligibility-calculator)');
            return;
        }
        const label = linkText.trim() || 'home loan eligibility calculator';
        const cleanUrl = linkUrl.trim();
        const tag = `<a href="${cleanUrl}">${label}</a>`;
        const currentValue = settings.calculator_notice_text || '';
        const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
        setSettings(prev => ({ ...prev, calculator_notice_text: updatedValue }));
        toast.success(`Inserted link: ${label}`);
        setShowLinkModal(false);
        setLinkText('');
        setLinkUrl('');
    };

    const LOAN_TYPE = 'home_purchase_loan';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [criteriaRes, settingsRes] = await Promise.all([
                axios.get(`/api/admin/eligibility/${LOAN_TYPE}`),
                axios.get(`/api/admin/eligibility/settings/${LOAN_TYPE}`)
            ]);

            setCriteriaList(criteriaRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(prev => ({
                    ...prev,
                    ...settingsRes.data.data,
                    calculator_notice_text: settingsRes.data.data.calculator_notice_text || DEFAULT_CALCULATOR_NOTICE_TEXT
                }));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load eligibility data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingCriteria) {
                await axios.put(`/api/admin/eligibility/${editingCriteria.id}`, formData);
                toast.success('Criteria updated successfully');
            } else {
                await axios.post('/api/admin/eligibility', formData);
                toast.success('Criteria created successfully');
            }
            setIsModalOpen(false);
            setEditingCriteria(null);
            fetchData();
        } catch (error) {
            console.error('Error saving criteria:', error);
            toast.error('Failed to save criteria');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, criteriaId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/eligibility/${deleteConfirm.criteriaId}`);
            toast.success('Criteria deleted successfully');
            setDeleteConfirm({ isOpen: false, criteriaId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting criteria:', error);
            toast.error('Failed to delete criteria');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/eligibility/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Eligibility Criteria</h1>
                    <p className="text-slate-500 mt-1">Manage the eligibility requirements for Home Purchase Loan</p>
                </div>
                <button
                    onClick={() => {
                        setEditingCriteria(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Criteria
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Home Purchase Loan Eligibility"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Eligibility for an Aham home purchase loan is simple..."
                        />
                    </div>

                    {/* Calculator Notice Paragraph with Link Inserter */}
                    <div className="md:col-span-2 border-t border-slate-100 pt-5 mt-2 space-y-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700">
                                    Calculator Notice Paragraph (After Subtitle, Before Cards)
                                </label>
                                <p className="text-xs text-slate-500">
                                    Inserts immediately below the introductory text and before the eligibility cards.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowLinkModal(!showLinkModal);
                                    setLinkText('');
                                    setLinkUrl('');
                                }}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-800 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors border border-primary-200"
                            >
                                <LinkIcon className="w-3.5 h-3.5" />
                                <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
                            </button>
                        </div>

                        {showLinkModal && (
                            <div className="bg-primary-50/80 border border-primary-200 rounded-xl p-3 mb-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-primary-900">Insert Link into Paragraph</span>
                                    <span className="text-[11px] text-primary-700">e.g. https://ahamhfc.com/customer-360-eligibility-calculator</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                                        <input
                                            type="text"
                                            value={linkText}
                                            onChange={e => setLinkText(e.target.value)}
                                            placeholder="e.g. home loan eligibility calculator"
                                            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                                        <input
                                            type="text"
                                            value={linkUrl}
                                            onChange={e => setLinkUrl(e.target.value)}
                                            placeholder="e.g. https://ahamhfc.com/customer-360-eligibility-calculator"
                                            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-primary-500"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => setShowLinkModal(false)}
                                        className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleInsertLink}
                                        className="px-3 py-1 text-xs font-bold bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-sm"
                                    >
                                        Insert &lt;a&gt; Tag
                                    </button>
                                </div>
                            </div>
                        )}

                        <textarea
                            rows="3"
                            value={settings.calculator_notice_text || ''}
                            onChange={(e) => setSettings({ ...settings, calculator_notice_text: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-normal"
                            placeholder='Not sure how much home loan you may qualify for? Use our <a href="https://ahamhfc.com/customer-360-eligibility-calculator">home loan eligibility calculator</a> to get an initial estimate before you apply.'
                        />
                        <p className="mt-1 text-[11px] text-slate-500">
                            Tip: HTML anchor tags and line breaks are fully supported. Use &ldquo;+ Insert Link&rdquo; to quickly format links.
                        </p>

                        {/* Live Preview */}
                        {settings.calculator_notice_text && (
                            <div className="mt-3 bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3 shadow-sm">
                                <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">PARAGRAPH LIVE PREVIEW</span>
                                    </div>
                                    <span className="text-xs text-slate-500">Live preview with clickable styled link</span>
                                </div>
                                <div
                                    className="text-slate-700 text-sm md:text-base leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors"
                                    dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(settings.calculator_notice_text) }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Criteria Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-48 bg-slate-100 rounded-2xl animate-pulse"></div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {criteriaList.map((criteria) => (
                        <EligibilityCard
                            key={criteria.id}
                            criteria={criteria}
                            onEdit={(item) => {
                                setEditingCriteria(item);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <EligibilityModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingCriteria(null);
                }}
                onSubmit={handleSubmit}
                initialData={editingCriteria}
                loading={submitting}
                loanType={LOAN_TYPE}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, criteriaId: null })}
                onConfirm={confirmDelete}
                title="Delete Criteria"
                message="Are you sure you want to delete this criteria? This action cannot be undone."
            />
        </div>
    );
};

export default HomePurchaseLoanEligibilityPage;
